import {
  takeEvery,
  put,
  select,
  all,
  take,
  race,
  call,
} from 'redux-saga/effects'
import {
  dimensions as trackDimensions,
  dataLayer,
  shouldTrack,
} from 'utils/analytics'
import { isEmpty } from 'ramda'
import {
  GET_USER_INFO,
  UPDATE_USER_ROLES_SUCCESS,
  CHANGE_USER_ROLE_SUCCESS,
  ADD_VEHICLE_TO_RECENTLY_VIEWED,
  RECENTLY_VIEWED_DATA_RECEIVED,
  RECENTLY_VIEWED_DATA_NOT_RECEIVED,
  getRecentlyViewedStorageKey,
  applyRecentlyViewedLocalCache,
  clearFavoriteRemovedNotification,
  clearFavoriteUpdatedNotification,
  getUser,
} from 'actions/user'
import { createSystemNotification } from 'actions/application'
import { getRecentlyViewedVehicles, getUserName } from 'selectors/user'
import { SET_SELECTED_ID } from 'actions/navigator'
import { SHOW_FAVOURITE_VEHICLES } from 'utils/constants'

// Persists the recently-viewed vehicles list to localStorage after every update
// so it survives page refreshes.
export function* watchPersistRecentlyViewed() {
  yield takeEvery(ADD_VEHICLE_TO_RECENTLY_VIEWED, persistRecentlyViewed)
}

function* persistRecentlyViewed() {
  const userName = yield select(getUserName)
  // Skip the write if identity is not yet known — avoids writing to the
  // unscoped fallback key and then losing the entry when the user-scoped
  // key is read on the next refresh.
  if (!userName) return
  const vehicles = yield select(getRecentlyViewedVehicles)
  const storageKey = getRecentlyViewedStorageKey(userName)
  try {
    // Guard against overwriting prior local history that hasn't been merged
    // into Redux yet (i.e. identity resolved but RECENTLY_VIEWED_DATA_RECEIVED
    // hasn't fired). Preserve any stored entries absent from the current Redux
    // list so they survive until the startup merge promotes them into the store.
    const stored = localStorage.getItem(storageKey)
    const storedVehicles = stored ? JSON.parse(stored) : []
    const reduxSerials = new Set((vehicles || []).map((v) => v.serialNumber))
    const unmerged = storedVehicles.filter(
      (v) => !reduxSerials.has(v.serialNumber)
    )
    const toWrite = [...(vehicles || []), ...unmerged].slice(0, 10)
    localStorage.setItem(storageKey, JSON.stringify(toWrite))
  } catch (e) {
    // localStorage may be unavailable (private browsing, storage quota exceeded)
  }
}

// Merges the user-scoped localStorage cache into the recently-viewed list once
// both prerequisites are satisfied: the server data has arrived AND the user
// identity is known. Uses `all` so either order of arrival is handled correctly.
export function* watchMergeRecentlyViewedCache() {
  yield all([
    take(GET_USER_INFO),
    race({
      received: take(RECENTLY_VIEWED_DATA_RECEIVED),
      failed: take(RECENTLY_VIEWED_DATA_NOT_RECEIVED),
    }),
  ])
  // Apply local cache regardless of whether the server request succeeded.
  // On failure, serverVehicles in mergeLocalVehicleCache will be [], so the
  // local cache is shown on its own rather than left empty.
  yield call(mergeLocalVehicleCache)
}

function* mergeLocalVehicleCache() {
  const userName = yield select(getUserName)
  const serverVehicles = yield select(getRecentlyViewedVehicles)
  const storageKey = getRecentlyViewedStorageKey(userName)
  try {
    const stored = localStorage.getItem(storageKey)
    const localVehicles = stored
      ? JSON.parse(stored).map((v) => ({ ...v, selected: false }))
      : []
    // Build lookup so server-authoritative viewedDate can override stale
    // client-cached timestamps (the DB VIEW_TS is the source of truth).
    const serverBySerial = {}
    ;(serverVehicles || []).forEach((v) => {
      if (v.viewedDate && !v.viewedDate.startsWith('0001')) {
        serverBySerial[v.serialNumber] = v
      }
    })
    const localSerials = new Set(localVehicles.map((v) => v.serialNumber))
    const merged = [
      ...localVehicles.map((v) => ({
        ...v,
        viewedDate:
          (serverBySerial[v.serialNumber] &&
            serverBySerial[v.serialNumber].viewedDate) ||
          v.viewedDate,
      })),
      ...(serverVehicles || []).filter(
        (v) => !localSerials.has(v.serialNumber)
      ),
    ].slice(0, 10)
    if (merged.length === 0) return
    yield put(applyRecentlyViewedLocalCache(merged))
    // Write the fully merged list back to localStorage so optimistic entries
    // that couldn't be persisted before identity resolved now survive refresh.
    localStorage.setItem(storageKey, JSON.stringify(merged))
  } catch (e) {
    // localStorage may be unavailable
  }
}
// exported for testing only — not part of public API
export { mergeLocalVehicleCache as mergeLocalVehicleCacheForTesting }

export function* analytics() {
  if (!shouldTrack()) return

  yield takeEvery(GET_USER_INFO, trackUserDetails)
}

function trackUserDetails({ data }) {
  if (!isEmpty(data)) {
    const { org, email, appRoles, userName } = data
    dataLayer({
      event: 'user-info',
      user: {
        userName,
        email,
        org,
        appRoles,
      },
    })
    trackDimensions({
      dimension1: userName,
      dimension2: email,
      dimension3: org,
      dimension4: appRoles.toString(),
    })
  }
}

// Clear favorite removed notification when favorites drawer is closed via navigator
export function* watchFavoriteDrawerClose() {
  yield takeEvery(SET_SELECTED_ID, clearNotificationOnDrawerClose)
}

function* clearNotificationOnDrawerClose(action) {
  const showRemovedNotification = yield select((state) =>
    state.getIn(['user', 'showFavoriteRemovedNotification'])
  )
  const showUpdatedNotification = yield select((state) =>
    state.getIn(['user', 'showFavoriteUpdatedNotification'])
  )
  // Clear notifications when navigating away from favorites drawer or closing it
  if (action.id !== SHOW_FAVOURITE_VEHICLES) {
    if (showRemovedNotification) {
      yield put(clearFavoriteRemovedNotification())
    }
    if (showUpdatedNotification) {
      yield put(clearFavoriteUpdatedNotification())
    }
  }
}

/**
 * After the user's own roles are updated successfully, refresh the user state
 * and show a success toast.
 */
export function* watchUpdateUserRoles() {
  yield takeEvery(UPDATE_USER_ROLES_SUCCESS, handleUpdateUserRolesSuccess)
}

function* handleUpdateUserRolesSuccess() {
  // Fire both the user refresh and the success toast immediately (optimistic UX).
  // The toast is shown regardless of whether the subsequent getUser() refresh
  // succeeds, because the role update itself has already committed on the server.
  // If getUser() fails, the stale roles state will be corrected on the next full
  // page load. This trade-off is intentional — getUser() failures are rare and
  // a full-page reload is the recovery path for stale auth state.
  yield put(getUser())
  yield put(
    createSystemNotification({
      notificationMessage: 'Roles updated successfully.',
    })
  )
}

/**
 * After a role change via the developer tool, refresh the user state.
 */
export function* watchChangeUserRole() {
  yield takeEvery(CHANGE_USER_ROLE_SUCCESS, handleChangeUserRoleSuccess)
}

function* handleChangeUserRoleSuccess() {
  yield put(getUser())
}
